import { Component, DestroyRef, inject, Input, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Observable } from "rxjs";

/**
 * A small text-input dialog for renaming the active grocery list. Rendered
 * inside {@link DialogWrapperComponent} via `ngComponentOutlet`, so it receives
 * its data (and the close callback) as `@Input`s rather than injecting the
 * dialog ref — matching the app's other form-in-a-wrapper dialogs (e.g.
 * add-budget-form, categories-add-edit).
 */
@Component({
  selector: "app-rename-list-dialog",
  templateUrl: "./rename-list-dialog.component.html",
})
export class RenameListDialogComponent implements OnInit {
  /** Current list name, pre-filled into the field. */
  @Input() currentName = "";
  /** Closes the surrounding dialog (supplied by the opener). */
  @Input() closeDialog!: CallableFunction;
  /**
   * Performs the rename with the trimmed new name when the user confirms. The
   * dialog closes only once the returned stream emits, and stays open (so the
   * user can retry, with the heading still showing the old name) if it errors.
   */
  @Input() onRename?: (name: string) => Observable<unknown>;

  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  formGroup!: FormGroup;
  /** True while a rename is in flight — keeps the form up and guards a double-submit. */
  submitting = false;

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      name: [this.currentName, [Validators.required, Validators.pattern(/\S/)]],
    });
  }

  submit(): void {
    this.formGroup.markAllAsTouched();
    if (!this.formGroup.valid || this.submitting) {
      return;
    }
    const name = (this.formGroup.controls["name"].value as string).trim();
    if (!this.onRename) {
      this.close();
      return;
    }
    // Keep the dialog open until the PATCH settles: close on success, and on
    // failure re-enable so the still-visible form can be retried.
    this.submitting = true;
    this.onRename(name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.close(),
        error: () => (this.submitting = false),
      });
  }

  private close(): void {
    if (typeof this.closeDialog === "function") {
      this.closeDialog();
    }
  }
}
